"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChevronLeft, ArrowRight } from "lucide-react";
import Link from "next/link";

type LineItem = { name: string; price: string };

const BASE_ITEMS: LineItem[] = [
  { name: "Old Fashioned", price: "$16" },
  { name: "Margarita", price: "$14" },
  { name: "Mezcal Negroni", price: "$18" },
  { name: "Topo Chico", price: "$5" },
];

const NEW_ITEMS: LineItem[] = [
  { name: "Tequila shot × 2", price: "$18" },
  { name: "Paloma", price: "$16" },
];

const BARS = [
  { name: "Main Bar", time: "11:08 PM" },
  { name: "Rooftop", time: "1:14 AM", isAdder: true },
  { name: "Service Bar", time: "12:42 AM" },
];

export default function LiveBarTabsHero() {
  const { t } = useLang();

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-[#0A0814] text-white">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/feature-live-bar-tabs.mp4" type="video/mp4" />
      </video>
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[#0A0814]/75 pointer-events-none" />
      <div
        className="absolute top-1/4 -left-32 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.30) 0%, transparent 60%)" }}
      />
      <div
        className="absolute bottom-0 -right-32 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(251,146,60,0.18) 0%, transparent 60%)" }}
      />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white/55 hover:text-white transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#FB923C" }}
        >
          {t("Live bar tabs · Across every station", "Bar tabs en vivo · En cada estación")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[40px] sm:text-[56px] md:text-[80px] lg:text-[96px] font-black leading-[0.98] tracking-[-0.04em] mb-6"
        >
          {t("One tab.", "Una tab.")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 50%, #FB923C 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("Five bartenders. Zero confusion.", "Cinco bartenders. Cero confusión.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-white/60 max-w-[680px] mx-auto leading-relaxed mb-12"
        >
          {t(
            "Customer opens a tab at the main bar at 11. Moves to the rooftop at 1. Closes back at the main bar at 2. The tab knows everything — every station sees the same tab in real time.",
            "El cliente abre tab en la main bar a las 11. Sube al rooftop a la 1. Cierra abajo a las 2. La tab lo sabe todo — cada estación ve la misma tab en tiempo real."
          )}
        </motion.p>

        {/* HERO: Triple bartender sync */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="relative mb-12"
        >
          {/* Sync indicator line */}
          <div className="absolute top-1/2 left-[15%] right-[15%] h-px -translate-y-1/2 hidden md:block pointer-events-none">
            <div
              className="h-full"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(244,114,182,0.4), transparent)",
              }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-[1200px] mx-auto">
            {BARS.map((bar, i) => (
              <BarTabCard
                key={bar.name}
                bar={bar}
                index={i}
                items={bar.isAdder ? [...BASE_ITEMS, ...NEW_ITEMS] : BASE_ITEMS}
                newItems={bar.isAdder ? NEW_ITEMS : []}
              />
            ))}
          </div>

          {/* Sync caption */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="text-center mt-8"
          >
            <div className="inline-flex items-center gap-2 bg-white/[0.04] backdrop-blur-xl border border-white/10 px-4 py-2.5 rounded-full">
              <div
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: "#F472B6" }}
              />
              <span className="text-[12px] font-bold text-white/85">
                {t("All bars. Same truth.", "Todas las barras. La misma verdad.")}{" "}
                <span style={{ color: "#F472B6" }}>{t("In real time", "En tiempo real")}</span>
              </span>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.6 }}
          className="text-center"
        >
          <Link
            href="/signup?vertical=nightlife"
            className="group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-2xl text-white"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
              boxShadow: "0 20px 60px rgba(124, 58, 237, 0.5)",
            }}
          >
            {t("Run real bar tabs →", "Corre tabs reales →")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function BarTabCard({
  bar,
  index,
  items,
  newItems,
}: {
  bar: { name: string; time: string; isAdder?: boolean };
  index: number;
  items: LineItem[];
  newItems: LineItem[];
}) {
  const total = items
    .map((i) => Number(i.price.replace(/[^0-9.]/g, "")))
    .reduce((s, n) => s + n, 0);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{
        duration: 0.6,
        delay: 0.7 + 0.15 * index,
        ease: [0.21, 0.47, 0.32, 0.98],
      }}
      className="relative"
    >
      {/* iPad-style horizontal frame */}
      <div
        className="rounded-[20px] p-[5px] shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
        style={{ backgroundColor: "#0a0a0a" }}
      >
        <div className="bg-[#0F0B1F] rounded-[16px] overflow-hidden border border-white/5">
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between border-b border-white/5">
            <div>
              <div className="text-[8px] font-black tracking-widest uppercase text-white/40">
                {bar.name}
              </div>
              <div className="text-[11px] font-black text-white">JAMES · Open</div>
            </div>
            <div className="flex items-center gap-1.5">
              <div
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ backgroundColor: bar.isAdder ? "#FB923C" : "#F472B6" }}
              />
              <span
                className="text-[8px] font-bold uppercase tracking-wider"
                style={{ color: bar.isAdder ? "#FB923C" : "#F472B6" }}
              >
                Live
              </span>
            </div>
          </div>

          {/* Items */}
          <div
            className="px-4 py-3 space-y-1.5"
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          >
            {items.map((item, i) => {
              const isNew = newItems.some((n) => n.name === item.name);
              return (
                <motion.div
                  key={`${item.name}-${i}`}
                  initial={isNew ? { opacity: 0, x: -10, backgroundColor: "rgba(46,204,113,0.18)" } : false}
                  animate={isNew ? { opacity: 1, x: 0, backgroundColor: "rgba(46,204,113,0)" } : undefined}
                  transition={isNew ? { duration: 1.5, delay: 1.0 } : undefined}
                  className="flex justify-between items-baseline text-[11px] rounded px-1.5 py-0.5"
                >
                  <span className={isNew ? "font-bold" : "text-white/65"} style={isNew ? { color: "#2ECC71" } : undefined}>
                    {item.name}
                    {isNew && (
                      <span className="ml-1.5 text-[8px] uppercase tracking-wider" style={{ color: "#2ECC71" }}>
                        +
                      </span>
                    )}
                  </span>
                  <span className={`tabular-nums ${isNew ? "font-black" : "text-white/85"}`} style={isNew ? { color: "#2ECC71" } : undefined}>
                    {item.price}
                  </span>
                </motion.div>
              );
            })}
          </div>

          {/* Total */}
          <div className="border-t border-dashed border-white/10 mx-4" />
          <div className="px-4 py-3 flex items-baseline justify-between">
            <span className="text-[8px] font-bold uppercase tracking-widest text-white/45">
              Total
            </span>
            <motion.span
              key={total}
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="text-[20px] font-black text-white tabular-nums"
            >
              ${total}
            </motion.span>
          </div>

          {/* Footer timestamp */}
          <div
            className="px-4 py-2 text-[8px] font-bold uppercase tracking-wider"
            style={{ color: bar.isAdder ? "#FB923C" : "rgba(255,255,255,0.35)" }}
          >
            {bar.isAdder
              ? `+2 added at ${bar.time}`
              : `Last update ${bar.time}`}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
